import { ColumnDef } from '@tanstack/react-table';
import { Copy, Eye, MoreHorizontal, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { createSortableColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { formatCurrency, getQuoteStatusLabel, Quote, QuoteStatus } from '../types/quote';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

const Quotes: React.FC = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  // Pagination and sorting
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<Quote | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  useEffect(() => {
    loadQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sortBy, sortOrder, debouncedSearch]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);

      const response = await fetch(`/api/quotes?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setQuotes(data.data.quotes || []);
        setPagination(data.data.pagination || { total: 0, page: 1, limit: 10, pages: 0 });
      }
    } catch (err) {
      console.error('Error loading quotes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (quote: Quote) => {
    setQuoteToDelete(quote);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!quoteToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${quoteToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        setDeleteDialogOpen(false);
        setQuoteToDelete(null);
        loadQuotes();
      } else {
        setDeleteDialogOpen(false);
        setErrorMessage(data.message || 'Failed to delete quote');
        setErrorDialogOpen(true);
      }
    } catch {
      setDeleteDialogOpen(false);
      setErrorMessage('An error occurred while deleting the quote');
      setErrorDialogOpen(true);
    }
  };

  const handleDuplicate = async (quote: Quote) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${quote.id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        navigate(`/quotes/${data.data.id}/edit`);
      } else {
        setErrorMessage(data.message || 'Failed to duplicate quote');
        setErrorDialogOpen(true);
      }
    } catch {
      setErrorMessage('An error occurred while duplicating the quote');
      setErrorDialogOpen(true);
    }
  };

  const handleSend = async (quote: Quote) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${quote.id}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        loadQuotes();
      } else {
        setErrorMessage(data.message || 'Failed to send quote');
        setErrorDialogOpen(true);
      }
    } catch {
      setErrorMessage('An error occurred while sending the quote');
      setErrorDialogOpen(true);
    }
  };

  const getStatusBadgeVariant = (status: QuoteStatus) => {
    switch (status) {
      case QuoteStatus.ACCEPTED:
      case QuoteStatus.CONVERTED:
        return 'default' as const;
      case QuoteStatus.SENT:
      case QuoteStatus.VIEWED:
        return 'secondary' as const;
      case QuoteStatus.READY:
        return 'warning' as const;
      case QuoteStatus.REJECTED:
      case QuoteStatus.EXPIRED:
        return 'destructive' as const;
      case QuoteStatus.DRAFT:
      default:
        return 'outline' as const;
    }
  };

  const columns: ColumnDef<Quote>[] = [
    {
      ...createSortableColumn<Quote>('quote_number', 'Quote #'),
      cell: ({ row }) => (
        <button
          onClick={() => navigate(`/quotes/${row.original.id}`)}
          className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
        >
          {row.original.quote_number}
        </button>
      ),
    },
    {
      ...createSortableColumn<Quote>('customer_name', 'Customer'),
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.customer_name}</div>
          {row.original.customer_email && (
            <div className="text-sm text-muted-foreground">{row.original.customer_email}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'property_name',
      header: 'Property',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.property_name || '-'}</div>
          {row.original.bedrooms && (
            <div className="text-sm text-muted-foreground">{row.original.bedrooms} bedrooms</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'total_rooms_count',
      header: 'Rooms',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.total_rooms_count || 0}</span>
      ),
    },
    {
      accessorKey: 'total_price',
      header: 'Total',
      cell: ({ row }) => {
        const monthly = row.original.monthly_total || 0;
        const months = row.original.rental_length_months || 6;
        const total = row.original.total_price || 0;
        return (
          <div>
            <span className="font-medium">{formatCurrency(total)}</span>
            <div className="text-xs text-muted-foreground">
              {formatCurrency(monthly)}/mo × {months}mo
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'total_budget',
      header: 'Budget',
      cell: ({ row }) => {
        const budget = row.original.total_budget;
        const total = row.original.total_price || 0;
        if (!budget) return <span className="text-muted-foreground">-</span>;
        const isOver = total > budget;
        return (
          <div>
            <span className="font-medium">{formatCurrency(budget)}</span>
            <div className={`text-xs ${isOver ? 'text-red-500' : 'text-green-600'}`}>
              {isOver ? `Over by ${formatCurrency(total - budget)}` : `Under by ${formatCurrency(budget - total)}`}
            </div>
          </div>
        );
      },
    },
    {
      ...createSortableColumn<Quote>('status', 'Status'),
      cell: ({ row }) => (
        <Badge variant={getStatusBadgeVariant(row.original.status)}>
          {getQuoteStatusLabel(row.original.status)}
        </Badge>
      ),
    },
    {
      ...createSortableColumn<Quote>('valid_until', 'Valid Until'),
      cell: ({ row }) => {
        const value = row.original.valid_until;
        if (!value) return <span className="text-muted-foreground">-</span>;
        const date = new Date(value);
        const isExpired = date < new Date();
        return (
          <span className={isExpired ? 'text-red-600' : ''}>
            {date.toLocaleDateString()}
          </span>
        );
      },
    },
    {
      ...createSortableColumn<Quote>('created_at', 'Created'),
      cell: ({ row }) => {
        const value = row.original.created_at;
        if (!value) return <span className="text-muted-foreground">-</span>;
        return new Date(value).toLocaleDateString();
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const quote = row.original;
        const canSend = quote.status === QuoteStatus.READY;
        const canEdit = [QuoteStatus.DRAFT, QuoteStatus.READY].includes(quote.status);

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/quotes/${quote.id}`)}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              {canEdit && (
                <DropdownMenuItem onClick={() => navigate(`/quotes/${quote.id}/edit`)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              {canSend && (
                <DropdownMenuItem onClick={() => handleSend(quote)}>
                  <Send className="h-4 w-4 mr-2" />
                  Send to Customer
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleDuplicate(quote)}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={() => handleDeleteClick(quote)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="quotes" />
      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Quotes</h1>
          <Button onClick={() => navigate('/quotes/create')}>
            <Plus className="h-4 w-4 mr-2" />
            Create Quote
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={quotes}
          loading={loading}
          searchKey="quote_number"
          searchPlaceholder="Search quotes..."
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          emptyMessage="No quotes found"
          serverSide={true}
          pagination={pagination}
          onPaginationChange={(newPage, newLimit) => { setPage(newPage); setLimit(newLimit); }}
          onSortingChange={(newSortBy, newSortOrder) => { setSortBy(newSortBy); setSortOrder(newSortOrder); setPage(1); }}
        />
      </main>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Quote"
        description={`Are you sure you want to delete quote ${quoteToDelete?.quote_number}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        variant="destructive"
      />

      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
        variant="destructive"
      />
    </div>
  );
};

export default Quotes;
